import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// project imports
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { EmsMemberAccountInterface } from 'features/eai/eaiTypes';
import { setCurrentEmsMemberAccountUser } from 'features/eai/thunks/emsMemberAccountUserThunks';
import { selectEmsMemberAccountList } from 'features/eai/selectors/emsMemberAccountSelectors';
import { setEmsMemberAccountList } from 'features/eai/eaiSlice';
import {
  accountLogin,
  accountLogout,
  setSelectedEmsMemberAccount
} from 'features/account/accountSlice';
import { sendAuditLog } from 'features/app/appThunks';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Typography
} from '@mui/material';
import { useKeycloak } from 'contexts/KeycloakContext';

// types
interface SelectEmsMemberAccountDialogProps {
  open: boolean;
  onSelectMemberAccount: () => void;
}

// ===============================|| SELECT EMS MEMBER ACCOUNT DIALOG ||=============================== //

const SelectEmsMemberAccountDialog = ({
  open,
  onSelectMemberAccount
}: SelectEmsMemberAccountDialogProps) => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const emsMemberAccountList = useAppSelector(selectEmsMemberAccountList);

  useEffect(() => {
    if (emsMemberAccountList.length === 1) {
      handleSelectMemberAccount(emsMemberAccountList[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emsMemberAccountList]);

  const handleSelectMemberAccount = (
    emsMemberAccount: EmsMemberAccountInterface
  ) => {
    const { emsMemberAccountUserId, id } = emsMemberAccount;

    dispatch(setSelectedEmsMemberAccount(emsMemberAccount));
    dispatch(
      setCurrentEmsMemberAccountUser(id, emsMemberAccountUserId, () => {
        keycloak?.updateToken(18000).then((refreshed) => {
          if (refreshed) {
            keycloak.loadUserInfo().then((info: any) => {
              dispatch(
                accountLogin({
                  accessToken: keycloak.token,
                  accountInfo: {
                    session_state: keycloak.tokenParsed?.session_state,
                    ...info
                  }
                })
              );
            });
          }
        });
        dispatch(setEmsMemberAccountList([]));
        onSelectMemberAccount();
      })
    );
  };

  const handleLogout = async () => {
    dispatch(
      sendAuditLog({ message: 'User Logout onAuthLogout' }, () =>
        keycloak?.logout()
      )
    );
    dispatch(accountLogout);
  };

  const getListItemContent = (
    item: EmsMemberAccountInterface
  ): React.ReactElement => (
    <ListItemText
      primary={item.name || ''}
      secondary={t(`module.eai.codebooks.${item.accountType}.title`)}
    />
  );

  return (
    <Dialog
      aria-labelledby='simple-dialog-title'
      open={open}
      maxWidth='xs'
      fullWidth
    >
      <>
        <DialogTitle id='dialog-title'>
          {t('dialogs.selectEmsMemberAccount.title')}
        </DialogTitle>

        <Divider
          sx={{
            opacity: 1,
            borderColor:
              theme.palette.mode === 'dark'
                ? theme.palette.dark.light + 15
                : theme.palette.grey[200]
          }}
        />

        {open && (
          <DialogContent>
            {emsMemberAccountList.length ? (
              <List>
                {emsMemberAccountList.map((item, index) => (
                  <React.Fragment key={index}>
                    <ListItemButton
                      onClick={() => handleSelectMemberAccount(item)}
                    >
                      {getListItemContent(item)}
                    </ListItemButton>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography variant='body2'>
                {t('dialogs.selectEmsMemberAccount.noSubjectDescriptionBegin')}
                <Button onClick={handleLogout} variant='text'>
                  {t('dialogs.selectEmsMemberAccount.noSubjectLogoutButton')}
                </Button>
              </Typography>
            )}
          </DialogContent>
        )}
      </>
    </Dialog>
  );
};

export default SelectEmsMemberAccountDialog;
